import { SquareServiceError } from '../errors'

export default {
  data: function() {
    return {
      loading: false,
      processing: false,
      debounceTimer: null,
      processingComplete: false
    }
  },
  computed: {
    currency: function() {
      return this.$store.getters['square/currencyCode']
    },
    isAnonymous: function() {
      try {
        if (typeof this.userid === 'undefined' || this.userid === null) {
          return true
        }
        else if (typeof this.userid === 'number') {
          return this.userid === 0
        }
        else if (typeof this.userid === 'string') {
          return this.userid === null || this.userid.trim() === '0' || this.userid.trim() === ''
        }
        else {
          return false
        }
      }
      catch (_err) {
        return true
      }
    },
    testMode: function() {
      return this.$store.getters['square/testMode']
    },
    showCardEntry: function() {
      return this.testMode && this.source !== 'default'
    },
    showCardInput: function() {
      return !this.testMode || (this.testMode && Object.prototype.hasOwnProperty.call(this.options, 'overrideSource') && this.options.overrideSource === 'default')
    },
    overrideCardSource: function() {
      try {
        if (process.env.NODE_ENV === 'development') {
          return (Object.prototype.hasOwnProperty.call(this.options, 'overrideSourceID') && this.options.overrideSourceID.value !== 'default') ? this.options.overrideSourceID.text : false
        }
        return false
      }
      catch (err) {
        return false
      }
    },
    debugPayment: function() {
      try {
        return this.testMode && this.options.requestOptions.debug
      }
      catch (err) {
        return false
      }
    },
    processingResult: function() {
      return this.$store.getters['square/processingResult']
    },
    paymentStatus: function() {
      return this.type === 'payment' && this.processingResult.payment ? this.processingResult.payment.status : '--'
    },
    type: function() {
      if (this.processingResult) {
        if (Object.prototype.hasOwnProperty.call(this.processingResult, 'payment')) {
          return 'payment'
        }
        else if (Object.prototype.hasOwnProperty.call(this.processingResult, 'card')) {
          return 'card'
        }
        else if (Object.prototype.hasOwnProperty.call(this.processingResult, 'type')) {
          return this.processingResult.type
        }
      }
      return null
    },
    cardSource: function() {
      try {
        if (!this.isAnonymous && this.selectedCard && this.selectedCard !== 'new') {
          return this.selectedCard
        }
        else if (this.testMode) {
          return (Object.prototype.hasOwnProperty.call(this.options, 'overrideSourceID') && this.options.overrideSourceID.value !== 'default') ? this.options.overrideSourceID.value : null
        }
        else {
          return null
        }
      }
      catch (_err) {
        return null
      }
    }
  },
  watch: {
    loading: function(val) {
      // console.log('SquarePaymentsMixin: loading state changed to:', val, 'emitting loading event')
      this.$emit('loading', val)
    },
    processing: function(val) {
      this.$emit('processing', val)
    }
  },
  methods: {
    initializePaymentForm: function(containerElementId) {
      // console.log('initializePaymentForm called with container:', containerElementId)
      return this.$store.dispatch('square/getIsConnected', { force: true })
        .catch(err => {
          console.error(`getIsConnected failed: ${err.message}`)
          this.$emit('connected', false)
          throw new SquareServiceError(err.message, { category: 'MS-SERVICE-ERROR', reason: 'Error communicating with the Square Payment Service' })
        })
        .then(status => {
          if (status) {
            return this.$store.dispatch('square/squareCard', { elementId: containerElementId })
              .catch(err => {
                console.error('Error initializing Square Card', err)
                this.$emit('connected', false)
                throw err
              })
          }
          else {
            console.error('Square not connected')
            this.$emit('connected', false)
            throw new SquareServiceError('Square Payment is not connected.', { category: 'CONNECTION', reason: 'Error Square Payment is not connected.' })
          }
        })
        .then(result => {
          return result
        })
    },
    loadSquarePayments: function() {
      // console.log('loadSquarePayments called, checking if Square SDK exists:', typeof window.Square)
      if (window.Square === undefined) {
        // console.log('Square SDK not loaded, adding script tag')
        const squarePaymentsSDK = document.createElement('script')
        if (this.$store.getters['square/testMode']) {
          squarePaymentsSDK.setAttribute('src', 'https://sandbox.web.squarecdn.com/v1/square.js')
        }
        else {
          squarePaymentsSDK.setAttribute('src', 'https://web.squarecdn.com/v1/square.js')
        }
        document.head.appendChild(squarePaymentsSDK)

        return new Promise((resolve, reject) => {
          (function checkLoaded() {
            setTimeout(() => {
              // console.log('Checking if Square SDK loaded:', typeof window.Square)
              if (window.Square !== undefined && window.Square.payments !== undefined) {
                // console.log('Square SDK loaded successfully')
                resolve(true)
              }
              else {
                checkLoaded()
              }
            }, 100)
          })()
        })
      }
      else {
        console.log('Square SDK already loaded')
        return Promise.resolve(true)
      }
    },
    cleanupSquareForm: function() {
      // Destroy existing Square Card instance if it exists
      if (this.SquareCard) {
        try {
          this.SquareCard.destroy()
        }
        catch (err) {
          // Ignore errors during cleanup
          console.warn('Error destroying Square Card:', err)
        }
        this.SquareCard = null
      }

      // Clear the card container DOM element and remove any Square iframes
      const cardContainer = document.getElementById('square-card-container')
      if (cardContainer) {
        cardContainer.innerHTML = ''
      }

      // Also remove any lingering Square iframes that might exist outside the container
      const iFrameTitle = 'Secure card form'
      const squareIframes = document.querySelectorAll(`iframe.sq-card-component, iframe[title="${iFrameTitle}"]`)
      squareIframes.forEach(iframe => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe)
        }
      })

      // Reset form state
      this.showPaymentButtonVal = false
    },
    processTerminalPayment: async function() {
      if (this.debounceTimer !== null) {
        return
      }
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null
      }, 1500)

      try {
        this.$emit('processing', true)
        this.processing = true

        // Get timeout from store or use default
        const timeoutDuration = this.$store.getters['squareTerminalPaymentTimeout'] || 30

        const response = await this.$store.dispatch('square/processTerminalPayment', {
          paymentRequestID: this.paymentRequest.id,
          userid: this.userid,
          storeCard: !this.isAnonymous && this.storeCreditCard,
          // Extended retry timeout callbacks
          onTimeout: (timeoutData) => {
            this.timeoutElapsedTime = timeoutData.elapsedTime
            this.extendWaitFunction = timeoutData.extendWait
            this.showTimeoutModal = true
          },
          onRetryAttempt: (retryData) => {
            // Optional: Track retry attempts
            if (this.debugPayment) {
              console.debug('Square Terminal Payment Retry:', retryData)
            }
          },
          getCancelFunction: (cancelFn) => {
            this.paymentCancelFunction = cancelFn
          },
          timeoutDuration: timeoutDuration * 1000 // Convert seconds to milliseconds
        })

        this.showTimeoutModal = false
        this.$store.commit('square/PROCESSING_RESULT', { type: 'payment', ...response })
        if (response.status) {
          // Some processes look for the receipt number in the receiptno property
          response.payment.receiptno = response.payment.receipt_no
          this.$emit('success', response.payment)
        }
        else if (!response.status && ['CANCELED', 'FAILED'].includes(response.checkout.status)) {
          this.$emit('failed', response)
        }
        else {
          this.$emit('error', response)
        }
        this.processingComplete = true
        this.$emit('processing', false)
        this.processing = false
      }
      catch (err) {
        // Ensure processing state is properly reset on any error
        this.$emit('processing', false)
        this.processing = false
        this.processingComplete = false

        if (!this.$store.getters['square/processingResult']) {
          this.$store.commit('square/PROCESSING_RESULT', { type: 'payment', status: false, error: err })
        }

        // Emit error event to parent component
        this.$emit('error', err)
      }
    },
    processPayment: async function(recaptchaResponse) {
      if (this.debounceTimer !== null) {
        return
      }
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null
      }, 1500)

      this.processing = true
      let verified = true
      if (this.$store.getters['square/simulatedRecaptchaResponse'] !== null) {
        recaptchaResponse = this.$store.getters['square/simulatedRecaptchaResponse']
      }
      if (recaptchaResponse !== undefined) {
        verified = await this.$store.dispatch('square/verifyRecaptchaResponse', recaptchaResponse)
      }
      if (!verified) {
        this.$emit('recaptcha-error')
      }

      // TODO: Implement the network request retry functionality simplar to other payment methods
      // let networkRequestRetryReporter
      // if (this.options.requestOptions) {
      //   networkRequestRetryReporter = this.options.requestOptions.networkRequestRetryReporter !== undefined ? this.options.requestOptions.networkRequestRetryReporter : null
      // }
      // const requestOptions = {
      //   paymentStage: 'square--processPayment',
      //   requestKeyName: 'processCardPayment',
      //   masterRetryRequestID: 'processingPayment',
      //   retryRequestIDPersistent: true,
      //   networkRequestRetryReporter: networkRequestRetryReporter
      // }
      try {
        this.$emit('processing', true)
        this.processing = true

        const paymentRequest = {
          paymentRequestID: this.paymentRequest.id,
          cardSource: this.cardSource,
          storeCard: !this.isAnonymous && this.storeCreditCard,
          userid: this.userid,
          verificationDetails: this.paymentRequest.verificationDetails,
          security: {
            recaptcha: recaptchaResponse
          }
        }
        if (this.debugPayment) {
          console.debug(`SquarePaymentRequest: ${JSON.stringify(paymentRequest, null, 2)}`)
        }
        const response = await this.$store.dispatch('square/processPayment', paymentRequest)
        this.$store.commit('square/PROCESSING_RESULT', { type: 'payment', ...response })

        if (response.status) {
          // Successful payment - clean up and complete
          this.cleanupSquareForm()
          // Some processes look for the receipt number in the receiptno property
          response.payment.receiptno = response.payment.receipt_no
          this.$emit('success', response.payment)
          this.processingComplete = true
        }
        else if (!response.status && response.payment.status === 'FAILED') {
          // Failed payment - don't set processingComplete to true, allow retry
          this.cleanupSquareForm()
          this.$emit('failure', response)
          // Don't set processingComplete = true for failed payments to allow form reinitialization
          this.processingComplete = false

          // Reset processing state to allow reinitializing the form
          if (typeof this.initializeSquareCardForm === 'function') {
            setTimeout(function() {
              this.processing = false
              this.$emit('processing', false)
              this.initializeSquareCardForm()
            }.bind(this), 1500)
          }
        }
        else {
          // Other error cases - clean up and complete
          this.cleanupSquareForm()
          this.$emit('error', response)
          this.processingComplete = true
        }

        // Only set final processing states if not a failed payment
        if (!(response.payment && response.payment.status === 'FAILED')) {
          this.processingComplete = true
        }
        this.$emit('processing', false)
        this.processing = false
      }
      catch (err) {
        // Ensure processing state is properly reset on any error
        this.$emit('processing', false)
        this.processing = false
        this.processingComplete = false

        if (err.name === 'SquareCardTokenizationError') {
          this.$store.commit('square/PROCESSING_RESULT', { type: 'payment', status: false, error: err })
        }
        else {
          if (!this.$store.getters['square/processingResult']) {
            this.$store.commit('square/PROCESSING_RESULT', { type: 'payment', status: false, error: err })
          }
        }
        if (err.name === 'SquarePaymentFailure') {
          this.$emit('failed', err)
        }
        else {
          // Emit error event to parent component
          this.$emit('error', err)
        }
      }
    }
  }
}
